import {BrandConfig} from "./BrandConfig";
import {setBrand} from "./BrandContext";
import {brandSlugFromUrl} from "./brandSlugFromUrl";

/**
 * How long to wait for the brand before giving up and rendering the default.
 *
 * The first render is blocked on this call, so an unreachable backend must not be able to hold
 * the payment page hostage. Two and a half seconds is long enough for a cold Lambda-ish first
 * hit and short enough that a borrower on a broken deploy still gets a working page.
 */
const FETCH_TIMEOUT_MS = 2500;

/**
 * Resolves the brand this page renders with, before the first paint.
 *
 * <p>This is the production mechanism, not a development convenience. Every deployed environment
 * serves the page as a static file — nginx answers `/` and `/payment-app/*`, and the backend
 * hands back that same file for a brand's path (BrandPageFilter) — so the `#simpl-brand-config`
 * block baked into the HTML is always the build-time default, whatever the link says. The brand
 * has to be asked for.
 *
 * <p>The backend shares an origin with the page in every deployed environment, so this costs one
 * same-origin GET and carries the session cookie without any CORS involvement.
 *
 * <p>Awaited before rendering rather than fetched inside a component: the brand color covers half
 * the viewport, and rendering first would flash the wrong brand on every load.
 *
 * <p>The address bar alone decides the brand: a link that names one (`pay.nflp.com/vista`)
 * always shows it, and a plain link always shows the current default. The session is never
 * consulted, because every tab of the browser shares it, so the brand last opened in one tab
 * would otherwise turn up in another. The payment flow never leaves the brand's path — its steps
 * are hash routes — so a reload or Stripe's return redirect lands on the same brand.
 */
export async function resolveBrand(): Promise<void> {
    const slug = brandSlugFromUrl(window.location.pathname, window.location.search);

    // Always asked for, even when the slug matches the brand baked into the HTML: that baked-in
    // brand is only the build-time default, and only a successful lookup marks the brand as
    // resolved, which is what lets requests name it.
    const path = slug
        ? `/api/brands/${encodeURIComponent(slug)}`
        : "/api/brands/default";

    const brand = await fetchBrand(path, slug);
    if (brand) setBrand(brand);
}

async function fetchBrand(path: string, slug: string | null): Promise<BrandConfig | null> {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const label = slug ? `Brand "${slug}"` : "The default brand";

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
        const response = await fetch(`${base}${path}`, {
            credentials: "include",
            signal: controller.signal
        });

        if (!response.ok) {
            console.warn(`${label} could not be loaded (${response.status}); using the default.`);
            return null;
        }

        // An unauthenticated request to the backend answers 302 -> /, which fetch follows into
        // the SPA's own HTML. That arrives as a perfectly successful response, so status alone
        // cannot distinguish it — the content type is what gives it away.
        const contentType = response.headers.get("content-type") || "";
        if (contentType.indexOf("application/json") === -1) {
            console.warn(
                `${label} request returned ${contentType || "no content type"} rather than JSON, ` +
                `which usually means it was redirected to a login or error page. Using the default.`);
            return null;
        }

        const brand = await response.json() as BrandConfig;
        // A brand without a usable theme would paint an undefined surface — worse than no brand.
        if (!brand || !brand.theme || !brand.theme.surface) {
            console.warn(`${label} arrived without a usable theme; using the default.`);
            return null;
        }
        return brand;
    } catch (e) {
        // Never let this stop the page rendering. Reported loudly because the failure is
        // otherwise invisible: the page simply shows the default brand, which reads as the slug
        // being ignored rather than the lookup being blocked.
        const aborted = e instanceof Error && e.name === "AbortError";
        console.error(
            aborted
                ? `${label} did not load within ${FETCH_TIMEOUT_MS}ms, so the default brand is being shown.`
                : `${label} could not be loaded, so the default brand is being shown. If this is a ` +
                  `CORS failure, check that ${window.location.origin} is in ` +
                  `application.security.allowed-origins on the backend.`,
            e);
        return null;
    } finally {
        clearTimeout(timer);
    }
}
